<template>
  <div class="worldList">
    <div
      ref="worldListWrapperLinkList"
      class="wrapper__link__list"
    >
      <div
        ref="worldListLinkList"
        class="link__list"
      >
        <template
          v-for="item in list"
          :key="item.key"
        >
          <NuxtLink
            :to="getPathLink(item)"
            :external="isExternalLink"
            class="link"
            :class="{ isArea: target === 'area' }"
          >
            <div class="text">
              <figure
                v-if="target === 'area'"
                class="flag"
              >
                <img
                  :src="`${runtimeConfig.public.gvsUrl}/onebox/img/world/flag/${item.cc}.webp`"
                  width="20"
                  height="15"
                  loading="lazy"
                  alt=""
                >
              </figure>
              <div class="inner">
                <p class="area">
                  {{ item.text }}
                </p>
              </div>
            </div>
            <div
              v-if="target === 'country' && item.wx"
              class="info"
            >
              <figure class="icon">
                <img
                  :src="'https://gvs.weathernews.jp/onebox/img/wxicon/'+ item.wx +'.webp'"
                  width="34"
                  height="25"
                  loading="lazy"
                  alt=""
                >
              </figure>
              <div class="temp">
                <p class="max">
                  {{ item.maxTemp }}℃
                </p>
                <p class="min">
                  {{ item.minTemp }}℃
                </p>
              </div>
            </div>
          </NuxtLink>
        </template>
      </div>
    </div>
    <button
      v-if="existShowMore"
      class="button"
      :class="{ isOpen: showAll }"
      @click="clickShowMore"
    >
      {{ showAll ? '少なく表示する' : `${targetName}をもっと見る` }}
    </button>
  </div>
</template>

<script setup>
const props = defineProps({
  list: {
    type: Array,
    required: true
  },
  areaId: {
    type: String,
    required: true
  },
  target: {
    type: String,
    required: true
  },
  targetName: {
    type: String,
    required: true
  }
})

const HEIGHT_SETTING = {
  area: {
    pc: 37 * 4,
    sp: 36 * 4
  },
  country: {
    pc: 42 * 4,
    sp: 42 * 4
  }
}

const worldListWrapperLinkList = useTemplateRef('worldListWrapperLinkList')
const worldListLinkList = useTemplateRef('worldListLinkList')

const runtimeConfig = useRuntimeConfig()
const { $isWebViewFlag: isWebViewFlag } = useNuxtApp()
const isExternalLink = ref(isWebViewFlag)

const getViewerType = () => {
  return window?.innerWidth ? window.innerWidth < 830 ? 'sp' : 'pc' : isWebViewFlag ? 'sp' : 'pc'
}

const showAll = ref(false)
const sleep = (msec) => new Promise((resolve) => setTimeout(resolve, msec))

const clickShowMore = () => {
  showAll.value = !showAll.value
  if (!showAll.value) {
    // グローバルヘッダーの中のコンテンツのpadding分ズレることへの対処
    // アプリではないスマホビューの場合はブラウザ分もズレるので、決め打ちでさらに対処
    const globalHeaderContentPadding = isWebViewFlag ? 15 : getViewerType() === 'pc' ? 120 : 20 + 10
    worldListWrapperLinkList.value.style.height = `${HEIGHT_SETTING[props.target][getViewerType()]}px`
    sleep(220).then(() => {
      window.scrollTo({
        top: worldListWrapperLinkList.value.offsetTop - globalHeaderContentPadding,
        behavior: 'smooth'
      })
    })
  } else {
    const height = `${worldListLinkList.value.offsetHeight}px`
    worldListWrapperLinkList.value.style.height = height
  }
}

const getPathLink = (item) => {
  if (props.target === 'area') {
    return isExternalLink.value ? `${runtimeConfig.app.baseURL}${props.areaId}/${item.key}` : `/${props.areaId}/${item.key}/`
  }
  return isExternalLink.value ? `${runtimeConfig.app.baseURL}${item.point_id.replace(/^\//, '')}` : item.point_id
}

const existShowMore = computed(() => props.list.length > 12)

onMounted(() => {
  const height = worldListLinkList.value.offsetHeight
  worldListWrapperLinkList.value.style.height = `${existShowMore.value ? Math.min(height, HEIGHT_SETTING[props.target][getViewerType()]) : height}px`
})
</script>

<style lang="scss" scoped>
.worldList {
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  gap: 7px;

  .wrapper__link__list {
    width: 100%;
    height: 144px;
    overflow: hidden;
    transition: height .2s ease-in-out;

    .link__list {
      display: flex;
      flex-wrap: wrap;
      border-left: 1px solid #CCC;

      .link {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 5px;
        width: 50%;
        padding: 6px;
        background-color: #FFF;
        border-bottom: 1px solid #CCC;
        border-right: 1px solid #CCC;
        &:first-child,
        &:nth-child(2) {
          border-top: 1px solid #CCC;
        }
        position: relative;
        padding-right: 20px;
      }
      .link.isArea {
        &::before {
          position: absolute;
          top: 50%;
          right: 5px;
          width: 9px;
          height: 14px;
          margin-top: -7px;
          background-image: url('~/assets/images/common/icon_arrow_blue.svg');
          background-position: center;
          background-repeat: no-repeat;
          background-size: 9px;
          content: '';
        }
      }
      .text {
        display: flex;
        align-items: center;
        gap: 3px;
        .inner {
          display: flex;
          flex-direction: column;
          gap: 4px;
          flex: 1;
        }
        .flag {
          aspect-ratio: 1;
          border: 1px solid #ccc;
          border-radius: 50%;
          overflow: hidden;
          img {
            display: block;
            filter: saturate(60%) brightness(130%);
            width: 20px;
            height: 100%;
            object-fit: cover;
          }
        }
        .area {
          color: #3569C0;
          font-size: 12px;
          font-weight: 600;
          line-height: 14px;
          text-decoration: underline;
        }
        .time {
          color: #606060;
          font-size: 10px;
          line-height: 1em;
        }
      }
      .info {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 3px;
        min-width: 63px;
        .icon {
          width: 30px;
          aspect-ratio: 34 / 25;
          img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
        }
        .temp {
          display: flex;
          flex-direction: column;
          gap: 3px;
          p {
            text-align: right;
            font-size: 12px;
            font-weight: 600;
            line-height: 1em;
            &.max {
              color: #F64D00;
            }
            &.min {
              color: #0075F3;
            }
          }
        }
      }
      @media (any-hover: hover) {
        .link {
          transition: opacity .2s ease-in-out;
          &:hover {
            opacity: .6;
          }
        }
      }
    }
  }
  .button {
    position: relative;
    width: 100%;
    padding: 10px 30px 10px 20px;
    border-radius: 100vmax;
    background-color: #6981AA;
    box-shadow: 0 4px 4px 0 rgb(0 0 0 / .25);
    color: #FFF;
    font-size: 16px;
    font-weight: 600;
    line-height: 18px;
    text-align: center;
    cursor: pointer;
    &::before {
      position: absolute;
      top: 50%;
      right: 10px;
      transform: translateY(-50%);
      width: 17px;
      height: 15px;
      background-color: #fff;
      clip-path: polygon(0 0, 100% 0, 50% 100%);
      content: '';
    }
    &.isOpen::before {
      clip-path: polygon(50% 0, 100% 100%, 0 100%);
    }
  }
  @media (any-hover: hover) {
    .button {
      transition: box-shadow .2s ease-in-out;
      &:hover {
        box-shadow: 0 4px 6px 1px rgb(0 0 0 / .45);
      }
    }
  }
}
.main {
  width: 100%;
  .worldList {
    width: 100%;
    @media screen and (min-width: 830px) {
      .wrapper__link__list {
        width: 100%;
        .link__list {
          gap: 7px;
          border-top: none;
          border-left: none;
          .link {
            display: flex;
            align-items: center;
            gap: 8px;
            width: calc((100% - 14px) / 3);
            padding: 3px 8px;
            background-color: #FFF;
            border: 1px solid #CCC;
            border-radius: 3px;
          }
          .text {
            .time {
              text-align: center;
            }
          }
        }
      }
      .button {
        width: 60%;
        margin-inline: auto;
      }
    }
  }
}
</style>
